import React from 'react';

interface SkeletonLoaderProps {
  className?: string;
  variant?: 'text' | 'circular' | 'rectangular';
  width?: string | number;
  height?: string | number;
  count?: number;
}

export const SkeletonLoader: React.FC<SkeletonLoaderProps> = ({
  className = '',
  variant = 'rectangular',
  width,
  height,
  count = 1
}) => {
  const baseClasses = 'relative overflow-hidden bg-gray-200 dark:bg-gray-700 before:absolute before:inset-0 before:-translate-x-full before:animate-shimmer before:bg-gradient-to-r before:from-transparent before:via-white/20 dark:before:via-white/10 before:to-transparent';

  const variantClasses = {
    text: 'h-4 rounded',
    circular: 'rounded-full',
    rectangular: 'rounded-lg'
  };

  const style: React.CSSProperties = {};
  if (width) style.width = typeof width === 'number' ? `${width}px` : width;
  if (height) style.height = typeof height === 'number' ? `${height}px` : height;

  const skeletonElements = Array.from({ length: count }, (_, index) => (
    <div
      key={index}
      className={`${baseClasses} ${variantClasses[variant]} ${className}`}
      style={style}
    />
  ));

  return count > 1 ? <div className="space-y-2">{skeletonElements}</div> : <>{skeletonElements}</>;
};

// Spezielle Skeleton-Komponenten für häufige Use-Cases
export const MemberSkeleton: React.FC<{ count?: number }> = ({ count = 3 }) => (
  <div className="space-y-3">
    {Array.from({ length: count }, (_, index) => (
      <div key={index} className="flex items-center space-x-4 p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <SkeletonLoader variant="circular" width={48} height={48} />
        <div className="flex-1 space-y-2">
          <SkeletonLoader variant="text" height={20} width="60%" />
          <SkeletonLoader variant="text" height={16} width="40%" />
        </div>
        <SkeletonLoader variant="rectangular" width={80} height={32} />
      </div>
    ))}
  </div>
);

export const StatCardSkeleton: React.FC<{ count?: number }> = ({ count = 4 }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
    {Array.from({ length: count }, (_, index) => (
      <div key={index} className="bg-gray-800 rounded-lg p-6 space-y-3 border border-gray-700">
        <SkeletonLoader variant="text" height={16} width="50%" />
        <SkeletonLoader variant="text" height={32} width="70%" />
        <SkeletonLoader variant="text" height={14} width="40%" />
      </div>
    ))}
  </div>
);

export const TableSkeleton: React.FC<{ rows?: number }> = ({ rows = 5 }) => (
  <div className="space-y-2">
    {Array.from({ length: rows }, (_, index) => (
      <div key={index} className="flex space-x-4 p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <SkeletonLoader variant="text" width="30%" height={20} />
        <SkeletonLoader variant="text" width="25%" height={20} />
        <SkeletonLoader variant="text" width="20%" height={20} />
        <SkeletonLoader variant="text" width="25%" height={20} />
      </div>
    ))}
  </div>
);

export const DashboardWidgetSkeleton: React.FC = () => (
  <div className="bg-gray-800 rounded-lg p-6 space-y-4 border border-gray-700">
    <div className="flex items-center justify-between">
      <SkeletonLoader variant="circular" width={40} height={40} />
      <SkeletonLoader variant="text" width="40%" height={24} />
    </div>
    <div className="space-y-2">
      <SkeletonLoader variant="text" height={16} width="80%" />
      <SkeletonLoader variant="text" height={16} width="60%" />
    </div>
  </div>
);

export default SkeletonLoader;
